import classnames from 'classnames';
import React, { memo, useCallback, useMemo, useRef } from 'react';

import { AvoidHeaderAnchor } from '../../components/AvoidHeaderAnchor';
import { failureToast } from '../../components/Toast';
import { frontConfig } from '../../config';
import { useConnectedRouter } from '../../hooks/useConnectedRouter';
import { useAppSelector } from '../../redux/hooks';
import { useCurrentScrollSection, useRegisterScrollSection } from '../../services/scrollSections';
import { handleError } from '../../utils/common-utils';
import { isMobile } from '../../utils/web';
import type { VoterInstance } from '../BottomActions/VoteBottomAction';
import { ThreadInteractiveMessage } from '../Thread/ThreadInteractiveMessage';
import { useNavToAnswer } from './thread-service';

interface Props {
  messageId: string;
}

export const ThreadFlattenMessage: React.FC<Props> = memo(function ThreadFlattenMessage(props) {
  const { messageId } = props;
  const message = useAppSelector(state => state.threads.messages?.[messageId]);
  if (!message) {
    throw new Error(
      `[bug] Undefined message in ThreadFlattenMessage although the parent component is supposed to guarantee that the message is defined`,
    );
  }
  const { query, push } = useConnectedRouter();
  const withDirectLink = useMemo(
    () => ({
      to: message.id ? 'message' : 'topic',
      params: {
        back: true,
        slug: query.slug as string,
        ...(message.id ? { messageId: message.id } : null),
      },
    }),
    [query.slug, message.id],
  );

  const navToAnswer = useNavToAnswer();

  const answer = useCallback(() => {
    if (!message.id) {
      const err = new Error('No message ID when opening the editor');
      handleError(err);
      if (frontConfig.IS_DEV) {
        throw err;
      } else {
        failureToast(
          "We couldn't open the editor, something went wrong. Please let us know so that we can fix it.",
        );
        return;
      }
    }
    navToAnswer(message.id);
  }, [message, navToAnswer]);

  const startEditing = useCallback(() => {
    push({
      pathname: withDirectLink.to,
      query: {
        ...withDirectLink.params,
        edit: true,
      },
    });
  }, [push, withDirectLink]);

  const messageRef = useRef<HTMLDivElement>(null);
  const voteRef = useRef<VoterInstance>(null); // probably useless, since answering redirects.

  useRegisterScrollSection(messageRef, {
    id: message?.id,
    author: message?.author,
    voteRef,
    nestedAnswersRef: undefined,
    messageId: message?.id,
    noBubbleFlow: true,
  });

  const isMob = isMobile();
  const currentScrollSection = useCurrentScrollSection();
  const isCurrentSection = currentScrollSection?.id === message?.id;

  return (
    <>
      <AvoidHeaderAnchor anchorName={`message_${messageId}`} />
      <div
        className={classnames(
          'ThreadFlattenMessage p-4 md:px-6 md:pb-4 relative rounded-[5px]',
          'border border-solid border-gray-210', // rounded-sm p-2 md:p-4
          isMob && isCurrentSection && 'shadow-[0_0_10px_rgb(188,188,188,0.5)] z-[1]',
        )}
        ref={messageRef}
      >
        {/* <ShowIdStatusForDebug messageId={message.id} /> */}
        <ThreadInteractiveMessage
          message={message}
          answer={answer}
          startEditing={startEditing}
          answeredAuthor={message?.parentMessage?.author}
          directLink={withDirectLink}
          voteRef={voteRef}
        />
      </div>
    </>
  );
});
